import React, { useState, useEffect } from 'react';
import { Header, Card, CardHeader, CardBody, Input, Select, Button } from '@tadagram/ui';
import { Save } from 'lucide-react';
import { apiClient } from '../api/client';
import { WorkerInfo, WorkerConfigUpdate } from '../types';
import { useAppStore } from '../store';
import { useTranslation } from 'react-i18next';

export const Settings: React.FC = () => {
  const { workerUuid, setWorkerUuid } = useAppStore();
  const { t } = useTranslation('settings');
  const [workers, setWorkers] = useState<WorkerInfo[]>([]);
  const [selectedWorker, setSelectedWorker] = useState<WorkerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [schedule, setSchedule] = useState('');
  const [enabled, setEnabled] = useState(true);

  const loadWorkers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getMyWorkers();
      setWorkers(data);
      
      // Auto-select current worker or first worker
      const current = data.find(w => w.worker_uuid === workerUuid) || data[0];
      if (current) {
        setSelectedWorker(current);
        if (!workerUuid) {
          setWorkerUuid(current.worker_uuid);
        }
      }
    } catch (err: any) {
      setError(err.message || t('saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Load workers on mount
  useEffect(() => {
    loadWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form when worker selected
  useEffect(() => {
    if (selectedWorker) {
      setSchedule(selectedWorker.schedule || '');
      setEnabled(selectedWorker.enabled);
    }
  }, [selectedWorker]);

  const handleWorkerChange = (uuid: string) => {
    const worker = workers.find(w => w.worker_uuid === uuid);
    if (worker) {
      setSelectedWorker(worker);
      setWorkerUuid(uuid);
    }
  };

  const handleSave = async () => {
    if (!selectedWorker) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const update: WorkerConfigUpdate = {
        schedule,
        enabled,
      };

      await apiClient.updateWorkerConfig(selectedWorker.worker_uuid, update);
      
      setSuccessMessage(t('savedSuccess'));
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Reload workers to get updated data
      await loadWorkers();
    } catch (err: any) {
      setError(err.message || t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="min-h-screen bg-tg-bg pb-20">
      <Header 
        title={t('pageTitle')} 
        subtitle={t('pageSubtitle')}
      />
      
      <div className="container max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Success Message */}
        {successMessage && (
          <div className="bg-tg-success/10 border border-tg-success/20 rounded-lg p-4">
            <p className="text-tg-success text-sm">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-tg-error/10 border border-tg-error/20 rounded-lg p-4">
            <p className="text-tg-error text-sm">{error}</p>
          </div>
        )}

        {/* Worker Selection */}
        <Card>
          <CardHeader title={t('selectWorker')} subtitle={undefined} action={undefined} />
          <CardBody>
            {loading ? (
              <p className="text-tg-hint">{t('loading')}</p>
            ) : workers.length === 0 ? (
              <p className="text-tg-hint">{t('noWorkers')}</p>
            ) : (
              <Select
                label="Worker"
                value={selectedWorker?.worker_uuid || ''}
                onChange={(e) => handleWorkerChange(e.target.value)}
                options={workers.map(w => ({
                  value: w.worker_uuid,
                  label: `${w.worker_name || w.worker_uuid} (${w.account_count} accounts)`
                }))}
              />
            )}
          </CardBody>
        </Card>

        {/* Worker Configuration */}
        {selectedWorker && (
          <>
            <Card>
              <CardHeader title={t('workerConfig')} subtitle={undefined} action={undefined} />
              <CardBody>
                <div className="space-y-4">
                  <Input
                    label={t('workerUuid')}
                    value={selectedWorker.worker_uuid}
                    disabled
                  />
                  
                  <Input
                    label={t('workerName')}
                    value={selectedWorker.worker_name || 'N/A'}
                    disabled
                  />

                  <Input
                    label={t('workerType')}
                    value={selectedWorker.worker_type || t('marketing')}
                    disabled
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-tg-hint mb-1">{t('status')}</p>
                      <p className="text-tg-text font-medium">
                        {enabled ? t('running') : t('pausedStatus')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-tg-hint mb-1">{t('accountCount')}</p>
                      <p className="text-tg-text font-medium">
                        {selectedWorker.account_count}
                      </p>
                    </div>
                  </div>

                  {selectedWorker.last_executed_at && (
                    <div>
                      <p className="text-sm text-tg-hint mb-1">{t('lastExecution')}</p>
                      <p className="text-tg-text text-sm">
                        {new Date(selectedWorker.last_executed_at).toLocaleString('vi-VN')}
                      </p>
                    </div>
                  )}

                  {selectedWorker.next_execution_at && (
                    <div>
                      <p className="text-sm text-tg-hint mb-1">{t('nextExecution')}</p>
                      <p className="text-tg-text text-sm">
                        {new Date(selectedWorker.next_execution_at).toLocaleString('vi-VN')}
                      </p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title={t('cronSchedule')} subtitle={undefined} action={undefined} />
              <CardBody>
                <div className="space-y-4">
                  <Input
                    label={t('cronExpression')}
                    value={schedule}
                    onChange={(e) => setSchedule(e.target.value)}
                    placeholder={t('cronPlaceholder')}
                    helperText={t('cronFormat')}
                  />

                  <div className="p-4 bg-tg-bg-secondary rounded-lg">
                    <p className="text-sm font-medium text-tg-text mb-2">
                      {t('cronExamples')}
                    </p>
                    <ul className="text-sm text-tg-hint space-y-1">
                      <li>{t('cronExample1')}</li>
                      <li>{t('cronExample2')}</li>
                      <li>{t('cronExample3')}</li>
                      <li>{t('cronExample4')}</li>
                      <li>{t('cronExample5')}</li>
                    </ul>
                  </div>

                  <Select
                    label={t('toggleWorker')}
                    value={enabled ? 'true' : 'false'}
                    onChange={(e) => setEnabled(e.target.value === 'true')}
                    options={[
                      { value: 'true', label: t('enabled') },
                      { value: 'false', label: t('disabled') }
                    ]}
                  />
                </div>
              </CardBody>
            </Card>
          </>
        )}

        {/* Save Button */}
        {selectedWorker && (
          <div className="flex justify-end">
            <Button 
              icon={<Save size={20} />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? t('saving') : t('saveSettings')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
